import { Decoder, field, succeed, array, number, fail, string, boolean, dateISO } from 'jsonous';
import { explicitMaybe } from '@execonline-inc/decoders';
import { Maybe } from 'maybeasy';
import { gte } from '@execonline-inc/numbers';
import { resourceDecoder } from '../Resource/Decoders';

// Positive number decoder helper
const positiveNumberDecoder: Decoder<number> = number.andThen((n) =>
  gte(0)(n) ? succeed(n) : fail(`Expected a non-negative number, but received ${n}`),
);

// Edit Learning Collection - represents a collection in the edit group context
export type AccessSource = 'organization' | 'client_group' | 'private';

export interface EditLearningCollection {
  id: number;
  name: string;
  experiencesCount: number;
  selected: boolean;
  accessSource: AccessSource;
}

// Edit Product License - represents a product license in the edit group context
export interface EditProductLicense {
  id: number;
  licenseName: Maybe<string>;
  licenseTypeName: string;
  quantityAvailable: number;
  startOn: Date;
  endOn: Date;
  selected: boolean;
}

// Group Edit Data - the full payload for editing a group
export interface GroupEditData {
  id: number;
  name: string;
  description: string;
  status: string;
  learningCollections: EditLearningCollection[];
  productLicenses: EditProductLicense[];
}

const accessSourceDecoder: Decoder<AccessSource> = string.andThen((s) =>
  s === 'organization' || s === 'client_group' || s === 'private'
    ? succeed<AccessSource>(s)
    : fail(`Invalid access_source: ${s}`),
);

const editLearningCollectionDecoder: Decoder<EditLearningCollection> = succeed({})
  .assign('id', field('id', positiveNumberDecoder))
  .assign('name', field('name', string))
  .assign('experiencesCount', field('experiences_count', positiveNumberDecoder))
  .assign('selected', field('selected', boolean))
  .assign('accessSource', field('access_source', accessSourceDecoder));

const editProductLicenseDecoder: Decoder<EditProductLicense> = succeed({})
  .assign('id', field('id', positiveNumberDecoder))
  .assign('licenseName', field('license_name', explicitMaybe(string)))
  .assign('licenseTypeName', field('license_type_name', string))
  .assign('quantityAvailable', field('quantity_available', number))
  .assign('startOn', field('start_on', dateISO))
  .assign('endOn', field('end_on', dateISO))
  .assign('selected', field('selected', boolean));

const groupEditDataPayloadDecoder: Decoder<GroupEditData> = succeed({})
  .assign('id', field('id', positiveNumberDecoder))
  .assign('name', field('name', string))
  .assign('description', field('description', string))
  .assign('status', field('status', string))
  .assign(
    'learningCollections',
    field('learning_collections', array(editLearningCollectionDecoder)),
  )
  .assign('productLicenses', field('product_licenses', array(editProductLicenseDecoder)));

export const groupEditDataDecoder = resourceDecoder(groupEditDataPayloadDecoder);
